// Gestión de casos — bandeja única + flujo en 4 pasos.
//
// Reemplaza a las pestañas "Revisión de casos" y "Reembolsos", que mostraban el MISMO
// caso en dos lugares: el operador lo veía en una, lo marcaba en la otra, y armaba de
// memoria lo que faltaba. Acá cada caso se recorre entero: qué pasó → qué variables
// tiene → a dónde lo llevo → ejecutar y cerrar.
//
// El backend ya calculaba la solución paso a paso (`revision.service` → `solucion`);
// esta pantalla la usa como hilo conductor en vez de esconderla en un modal.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Typography, Button, Steps, Alert, Descriptions,
  Input, Radio, Row, Col, Statistic, Empty, Spin, message, Tooltip
} from 'antd';
import {
  ReloadOutlined, ArrowLeftOutlined, ArrowRightOutlined, DollarOutlined,
  FileTextOutlined, CheckCircleOutlined, WarningOutlined, UserOutlined, CreditCardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import OpsV2 from '../../../../service/ops.service';
import DetalleCobro from './DetalleCobro';
import AdminV2 from '../../../../service/admin.service';

const { Text, Title, Paragraph } = Typography;

const clp = n => '$' + Number(n || 0).toLocaleString('es-CL');
const limpiar = s => String(s ?? '').replace(/\s+/g, ' ').trim();

const COLOR_TIPO = {
  cobro_duplicado: 'red',
  inscripcion_parcial: 'volcano',
  falla_inscripcion: 'volcano',
  matricula_duplicada: 'orange',
  sobrecupo: 'gold',
  boleta: 'blue'
};

const EST_REEMBOLSO = {
  no_aplica: { color: 'default', label: 'Sin marcar' },
  pendiente: { color: 'orange', label: 'Pendiente' },
  reembolsado: { color: 'green', label: 'Reembolsado' }
};

export default function GestionCasos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [caso, setCaso] = useState(null);       // caso elegido en la bandeja
  const [detalle, setDetalle] = useState(null); // paso 2 en adelante
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [paso, setPaso] = useState(0);
  const [salida, setSalida] = useState(null);   // decisión del paso 3

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      setData(await OpsV2.casos());
    } catch (e) {
      message.error(e?.error || e?.message || 'No se pudo cargar la bandeja');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirCaso = async (row) => {
    setCaso(row);
    setPaso(0);
    setSalida(null);
    setDetalle(null);
    setCargandoDetalle(true);
    try {
      setDetalle(await OpsV2.detalleCaso(row.origen, row.id));
    } catch (e) {
      message.error(e?.error || 'No se pudo cargar el detalle del caso');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const volverABandeja = () => { setCaso(null); setDetalle(null); setPaso(0); setSalida(null); cargar(); };

  // ── Bandeja ────────────────────────────────────────────────────────────────
  if (!caso) {
    const r = data?.resumen || {};
    const columns = [
      {
        title: 'Caso', dataIndex: 'etiqueta', width: 180,
        render: (t, row) => <Tag color={COLOR_TIPO[row.tipo] || 'default'}>{t}</Tag>
      },
      { title: 'Código', dataIndex: 'codigo', width: 140, render: c => <Text code>{c || '—'}</Text> },
      { title: 'Cliente', dataIndex: 'cliente', ellipsis: true, render: c => limpiar(c) || '—' },
      {
        title: 'Origen', dataIndex: 'origen', width: 100,
        render: o => <Tag>{o === 'venta' ? 'Presencial' : 'Online'}</Tag>
      },
      { title: 'Cobrado', dataIndex: 'monto', width: 110, align: 'right', render: m => clp(m) },
      {
        title: 'A devolver', dataIndex: 'montoADevolver', width: 120, align: 'right',
        render: m => m > 0 ? <Text type="danger" strong>{clp(m)}</Text> : <Text type="secondary">—</Text>
      },
      {
        title: 'Reembolso', dataIndex: 'reembolso', width: 130,
        render: rb => <Tag color={EST_REEMBOLSO[rb?.estado]?.color}>{EST_REEMBOLSO[rb?.estado]?.label || rb?.estado}</Tag>
      },
      { title: 'Detectado', dataIndex: 'fecha', width: 130, render: f => f ? dayjs(f).format('DD/MM HH:mm') : '—' },
      {
        title: '', width: 110, fixed: 'right',
        render: (_, row) => <Button type="primary" size="small" onClick={() => abrirCaso(row)}>Gestionar</Button>
      }
    ];

    return (
      <div>
        <Space style={{ marginBottom: 12 }}>
          <Button icon={<ReloadOutlined />} onClick={cargar} loading={loading}>Refrescar</Button>
          {data?.rango && (
            <Text type="secondary">
              Desde el lunes {dayjs(data.rango.desde).format('DD/MM')} · lo anterior queda fuera a propósito
            </Text>
          )}
        </Space>

        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col xs={8}><Card size="small"><Statistic title="Casos abiertos" value={r.abiertos ?? 0} /></Card></Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Por devolver" value={r.porDevolver ?? 0} prefix={<DollarOutlined />}
                valueStyle={{ color: r.porDevolver ? '#cf1322' : '#3f8600' }}
                formatter={v => clp(v)} />
            </Card>
          </Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Reembolsos pendientes" value={r.reembolsosPendientes ?? 0}
                valueStyle={{ color: r.reembolsosPendientes ? '#fa8c16' : '#3f8600' }} />
            </Card>
          </Col>
        </Row>

        <Card size="small">
          <Table
            rowKey="id" size="small" loading={loading}
            columns={columns} dataSource={data?.casos || []}
            pagination={{ pageSize: 15, size: 'small' }}
            scroll={{ x: 1150 }}
            locale={{ emptyText: <Empty description="Sin casos abiertos esta semana" /> }}
          />
        </Card>
      </div>
    );
  }

  // ── Flujo del caso ─────────────────────────────────────────────────────────
  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={volverABandeja}>Volver a la bandeja</Button>
        <Tag color={COLOR_TIPO[caso.tipo] || 'default'}>{caso.etiqueta}</Tag>
        <Text code>{caso.codigo}</Text>
        <Text>{limpiar(caso.cliente)}</Text>
      </Space>

      <Steps
        current={paso} size="small" style={{ marginBottom: 16 }}
        items={[
          { title: 'Revisar' },
          { title: 'Variables' },
          { title: 'Decidir' },
          { title: 'Ejecutar' }
        ]}
      />

      <Card size="small" style={{ minHeight: 320 }}>
        {cargandoDetalle
          ? <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          : (
            <>
              {paso === 0 && <PasoRevisar caso={caso} detalle={detalle} />}
              {paso === 1 && <PasoVariables caso={caso} detalle={detalle} />}
              {paso === 2 && <PasoDecidir detalle={detalle} salida={salida} setSalida={setSalida} />}
              {paso === 3 && (
                <PasoEjecutar
                  caso={caso} detalle={detalle} salida={salida}
                  onListo={volverABandeja}
                  onRecargarDetalle={() => abrirCaso(caso)}
                />
              )}
            </>
          )}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <Button disabled={paso === 0} icon={<ArrowLeftOutlined />} onClick={() => setPaso(p => p - 1)}>
          Anterior
        </Button>
        {paso < 3 && (
          <Button
            type="primary" icon={<ArrowRightOutlined />}
            disabled={cargandoDetalle || (paso === 2 && !salida)}
            onClick={() => setPaso(p => p + 1)}
          >
            {paso === 2 ? 'Ir a ejecutar' : 'Siguiente'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Paso 1 · Revisar ─────────────────────────────────────────────────────────
function PasoRevisar({ caso, detalle }) {
  const plata = detalle?.plata;
  return (
    <>
      <Title level={5} style={{ marginTop: 0 }}>Qué pasó</Title>
      <Alert
        type={caso.montoADevolver > 0 ? 'error' : 'warning'}
        showIcon icon={<WarningOutlined />}
        message={detalle?.solucion?.resumen || caso.motivo || caso.etiqueta}
        description={caso.motivo && detalle?.solucion?.resumen ? caso.motivo : undefined}
        style={{ marginBottom: 16 }}
      />
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Cliente">{limpiar(caso.cliente) || '—'}</Descriptions.Item>
        <Descriptions.Item label="RUT">{caso.rut || '—'}</Descriptions.Item>
        <Descriptions.Item label="Origen">{caso.origen === 'venta' ? 'Venta presencial' : 'Compra online'}</Descriptions.Item>
        <Descriptions.Item label="Detectado">{caso.fecha ? dayjs(caso.fecha).format('DD/MM/YYYY HH:mm') : '—'}</Descriptions.Item>
        <Descriptions.Item label="Cobrado">{clp(plata?.cobrado ?? caso.monto)}</Descriptions.Item>
        <Descriptions.Item label="Servicio prestado">{plata?.servido != null ? clp(plata.servido) : '—'}</Descriptions.Item>
        <Descriptions.Item label="A devolver">
          {caso.montoADevolver > 0
            ? <Text type="danger" strong>{clp(caso.montoADevolver)}</Text>
            : <Text type="secondary">Nada por devolver</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Autorización">{plata?.auth || caso.auth || '—'}</Descriptions.Item>
      </Descriptions>
    </>
  );
}

// ── Paso 2 · Variables ───────────────────────────────────────────────────────
function PasoVariables({ caso, detalle }) {
  const items = detalle?.items || [];
  const docs = detalle?.documentos || [];
  const anulados = detalle?.documentosAnulados || [];
  const plata = detalle?.plata;

  return (
    <>
      <Title level={5} style={{ marginTop: 0 }}>Todo lo que define el caso</Title>

      <Text type="secondary">Personas de la compra</Text>
      <Table
        size="small" rowKey={(r, i) => r.inscripcionId || r.rut || i} pagination={false}
        style={{ marginTop: 8, marginBottom: 16 }}
        dataSource={items}
        locale={{ emptyText: 'Sin ítems registrados' }}
        columns={[
          { title: 'Inscrito', render: (_, r) => <Space><UserOutlined />{limpiar(r.inscrito?.nombre) || '—'}</Space> },
          { title: 'RUT', dataIndex: ['inscrito', 'rut'], width: 120 },
          { title: 'Taller', dataIndex: 'taller', ellipsis: true },
          {
            title: 'Estado', dataIndex: 'estadoItem', width: 170,
            render: e => {
              const m = {
                ok: ['green', 'Inscrito y facturado'],
                sin_boleta: ['orange', 'Sin boleta'],
                sin_inscripcion: ['red', 'Sin inscripción'],
                no_facturable: ['default', 'No facturable'],
                inscrito_por_otra_orden: ['red', 'Inscrito por otra orden']
              }[e]
                // Si aparece un estado nuevo, al menos que se lea como frase y no como
                // `snake_case` crudo en medio de una tabla en español.
                || ['default', e ? String(e).replace(/_/g, ' ') : '—'];
              return <Tag color={m[0]}>{m[1]}</Tag>;
            }
          },
          {
            title: 'Cupo', width: 110,
            render: (_, r) => r.inscripcion?.sobrecupo
              ? <Tag color="gold">Sobrecupo</Tag>
              : (r.inscripcion ? <Tag color="green">Tomado</Tag> : <Tag>—</Tag>)
          },
          {
            title: 'Duplicado', width: 200,
            render: (_, r) => r.inscripcionDeOtraOrden
              ? <Tooltip title={`Ya inscrito por ${r.inscripcionDeOtraOrden.orden}`}>
                  <Tag color="red">También en {r.inscripcionDeOtraOrden.orden}</Tag>
                </Tooltip>
              : <Text type="secondary">—</Text>
          }
        ]}
      />

      <Text type="secondary">Boletas vigentes</Text>
      <Table
        size="small" rowKey="folio" pagination={false}
        style={{ marginTop: 8, marginBottom: 16 }}
        dataSource={docs}
        locale={{
          emptyText: anulados.length > 0
            ? 'Ninguna vigente — las que hubo se anularon con nota de crédito (abajo)'
            : 'Sin boletas emitidas'
        }}
        columns={[
          { title: 'Folio', dataIndex: 'folio', width: 110, render: f => <Text code>{f}</Text> },
          { title: 'Monto', dataIndex: 'monto', width: 110, align: 'right', render: m => clp(m) },
          { title: 'Emitida', dataIndex: 'fecha', width: 180, render: f => f ? dayjs(f).format('DD/MM/YYYY HH:mm') : '—' },
          {
            // Con título y sin ancho: es la columna que estira, así que las tres
            // anteriores dejan de quedar apiñadas contra el borde izquierdo.
            title: 'Documento', render: (_, r) => r.urlPdf
              ? <a href={r.urlPdf} target="_blank" rel="noreferrer">Ver PDF</a>
              : <Text type="secondary">—</Text>
          }
        ]}
      />

      {anulados.length > 0 && (
        <>
          <Text type="secondary">Boletas anuladas con nota de crédito</Text>
          <Table
            size="small" rowKey="folio" pagination={false}
            style={{ marginTop: 8, marginBottom: 16 }}
            dataSource={anulados}
            columns={[
              {
                // 190 y `nowrap`: con 130 el folio se partía en dos líneas ("3269"/"37")
                // porque el tag le comía el ancho, y un folio cortado no se puede buscar
                // en Bsale.
                title: 'Folio anulado', dataIndex: 'folio', width: 190,
                render: f => (
                  <Space size={6} style={{ whiteSpace: 'nowrap' }}>
                    <Text delete code>{f}</Text>
                    <Tag color="red">Anulada</Tag>
                  </Space>
                )
              },
              { title: 'Monto', dataIndex: 'monto', width: 110, align: 'right', render: m => clp(m) },
              {
                title: 'Nota de crédito', dataIndex: 'notaCreditoFolio', width: 140,
                render: (f, r) => f
                  ? (r.notaCreditoUrlPdf
                    ? <a href={r.notaCreditoUrlPdf} target="_blank" rel="noreferrer">N° {f}</a>
                    : <Text code>N° {f}</Text>)
                  : '—'
              },
              {
                title: 'Anulada', width: 190,
                render: (_, r) => r.anuladoEn
                  ? (
                    <Space direction="vertical" size={0}>
                      <Text style={{ fontSize: 13 }}>{dayjs(r.anuladoEn).format('DD/MM/YYYY HH:mm')}</Text>
                      {r.anuladoPor && <Text type="secondary" style={{ fontSize: 12 }}>{r.anuladoPor}</Text>}
                    </Space>
                  )
                  : '—'
              },
              // El motivo va último y sin ancho fijo: es lo único de largo variable, así
              // que absorbe el espacio sobrante en vez de dejar un hueco al medio.
              { title: 'Motivo', dataIndex: 'motivo', ellipsis: true }
            ]}
          />
        </>
      )}

      {(plata?.items || []).length > 0 && (
        <>
          <Text type="secondary">Desglose del cobro — la evidencia detrás del "a devolver"</Text>
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            {/* El pie de DetalleCobro cuadra cobrado = entregado + a devolver, y por eso
                necesita los tres montos y el auth. Pasándole solo los ítems, el resumen
                salía en $0 y "sin auth" debajo de un desglose correcto. */}
            <DetalleCobro row={{
              codigo: caso.codigo,
              detalleItems: plata.items,
              monto: plata.cobrado,
              montoServido: plata.servido,
              montoAReembolsar: plata.aDevolver,
              auth: plata.auth,
              motivo: caso.motivo,
              tipoRevision: caso.tipo,
              reembolso: plata.reembolso
            }} />
          </div>
        </>
      )}

      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Cobrado">{clp(plata?.cobrado ?? caso.monto)}</Descriptions.Item>
        <Descriptions.Item label="Medio de pago">{plata?.medioPago || '—'}</Descriptions.Item>
        <Descriptions.Item label="Autorización">{plata?.auth || '—'}</Descriptions.Item>
        <Descriptions.Item label="Tarjeta">{plata?.tarjeta ? `•••• ${plata.tarjeta}` : '—'}</Descriptions.Item>
        <Descriptions.Item label="Reembolso">
          <Tag color={EST_REEMBOLSO[plata?.reembolso?.estado]?.color}>
            {EST_REEMBOLSO[plata?.reembolso?.estado]?.label || 'Sin marcar'}
          </Tag>
          {plata?.reembolso?.medio && <Tag>{plata.reembolso.medio}</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="A devolver">
          {plata?.aDevolver > 0 ? <Text type="danger" strong>{clp(plata.aDevolver)}</Text> : '—'}
        </Descriptions.Item>
      </Descriptions>
    </>
  );
}

// ── Paso 3 · Decidir ─────────────────────────────────────────────────────────
function PasoDecidir({ detalle, salida, setSalida }) {
  const aDevolver = detalle?.plata?.aDevolver || 0;
  const faltaInscribir = (detalle?.items || []).some(i => i.estadoItem === 'sin_inscripcion');
  const faltaBoleta = (detalle?.items || []).some(i => i.estadoItem === 'sin_boleta');
  // El `ojo` de cada paso es la advertencia que evita el error caro: devolver el total
  // cuando parte del servicio sí se prestó.
  const avisos = (detalle?.solucion?.pasos || []).map(p => p.ojo).filter(Boolean);

  const opciones = [
    aDevolver > 0 && {
      key: 'reembolso',
      titulo: `Reembolsar ${clp(aDevolver)}`,
      desc: 'Devolver lo cobrado de más y dejarlo trazado. En el siguiente paso eliges por dónde.',
      recomendada: true
    },
    faltaInscribir && {
      key: 'completar',
      titulo: 'Completar la inscripción',
      desc: 'Ya pagó: si el horario está lleno entra como sobrecupo y coordinación lo revisa.',
      recomendada: aDevolver === 0
    },
    faltaBoleta && {
      key: 'boleta',
      titulo: 'Emitir la boleta que falta',
      desc: 'Está inscrito y pagó, así que el documento corresponde.',
      recomendada: aDevolver === 0 && !faltaInscribir
    },
    {
      key: 'cerrar',
      titulo: 'Cerrar sin más acción',
      desc: 'El caso ya está regularizado o es un falso positivo. Queda en la auditoría quién lo cerró.'
    }
  ].filter(Boolean);

  return (
    <>
      <Title level={5} style={{ marginTop: 0 }}>¿A dónde lleva este caso?</Title>
      {detalle?.solucion?.resumen && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Lo que sugiere el sistema" description={detalle.solucion.resumen} />
      )}

      <Radio.Group value={salida} onChange={e => setSalida(e.target.value)} style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {opciones.map(o => (
            <Card key={o.key} size="small" hoverable
              style={{ borderColor: salida === o.key ? '#1677ff' : undefined }}
              onClick={() => setSalida(o.key)}>
              <Radio value={o.key}>
                <Space direction="vertical" size={0}>
                  <Space>
                    <Text strong>{o.titulo}</Text>
                    {o.recomendada && <Tag color="blue">Recomendado</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>{o.desc}</Text>
                </Space>
              </Radio>
            </Card>
          ))}
        </Space>
      </Radio.Group>

      {avisos.length > 0 && (
        <Alert type="warning" style={{ marginTop: 16 }} showIcon
          message="Ojo antes de decidir"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {avisos.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          }
        />
      )}
    </>
  );
}

// ── Paso 4 · Ejecutar ────────────────────────────────────────────────────────
function PasoEjecutar({ caso, detalle, salida, onListo, onRecargarDetalle }) {
  const [enviando, setEnviando] = useState(false);
  const [notas, setNotas] = useState('');
  const [medio, setMedio] = useState('transbank');

  const aDevolver = detalle?.plata?.aDevolver || 0;
  const docs = detalle?.documentos || [];

  const marcar = async (estado) => {
    setEnviando(true);
    try {
      await OpsV2.marcarReembolso(caso.id, { estado, medio, notas });
      message.success(estado === 'reembolsado' ? 'Reembolso registrado' : 'Marcado como pendiente');
      onListo();
    } catch (e) {
      message.error(e?.error || 'No se pudo marcar el reembolso');
    } finally {
      setEnviando(false);
    }
  };

  // 'pago_online' | 'venta_presencial': es como el backend nombra las dos ventas en
  // los endpoints de revisión, distinto del 'pago'/'venta' que usa la bandeja.
  const subtipoAPI = caso.origen === 'venta' ? 'venta_presencial' : 'pago_online';
  const pendientes = detalle?.pendientes || [];

  // ── Completar inscripción ──────────────────────────────────────────────────
  if (salida === 'completar') {
    const completar = async (pendiente) => {
      setEnviando(true);
      try {
        const out = await AdminV2.completarInscripcion(caso.id, { idx: pendiente.idx });
        message.success(
          `${limpiar(pendiente.nombre)} quedó inscrito` +
          (out?.sobrecupo ? ' (con sobrecupo: revisar el horario)' : '') +
          (out?.boletaEncolada ? ' · la boleta volvió a la cola' : '')
        );
        if (out?.pendientesRestantes === 0) onListo(); else onRecargarDetalle();
      } catch (e) {
        message.error(e?.error || e?.message || 'No se pudo completar la inscripción');
      } finally {
        setEnviando(false);
      }
    };

    return (
      <>
        <Title level={5} style={{ marginTop: 0 }}>Completar la inscripción</Title>
        {pendientes.length === 0
          ? <Empty description="Este caso no tiene inscripciones pendientes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="Ya pagó. Si el horario está lleno entra igual como sobrecupo y el cupo queda marcado para coordinación." />
              <Table
                size="small" rowKey="idx" pagination={false} dataSource={pendientes}
                columns={[
                  { title: 'Inscrito', render: (_, r) => limpiar(r.nombre) || '—' },
                  { title: 'RUT', dataIndex: 'rut', width: 120 },
                  { title: 'Taller', dataIndex: 'taller', ellipsis: true },
                  { title: 'Monto', dataIndex: 'monto', width: 100, align: 'right', render: m => clp(m) },
                  {
                    title: '', width: 130,
                    render: (_, r) => (
                      <Button type="primary" size="small" loading={enviando} onClick={() => completar(r)}>
                        Inscribir
                      </Button>
                    )
                  }
                ]}
              />
            </>
          )}
      </>
    );
  }

  // ── Reintentar boleta ──────────────────────────────────────────────────────
  if (salida === 'boleta') {
    const reintentar = async () => {
      setEnviando(true);
      try {
        const out = await AdminV2.reintentarBoleta(subtipoAPI, caso.id);
        if (out?.ok) message.success(`Boleta emitida: ${out.generados || 0} documento(s)`);
        else if (out?.motivoSkip) message.warning(`Saltado: ${out.motivoSkip}`);
        else message.error(`Falló: ${out?.errores || 0} error(es)`);
        onRecargarDetalle();
      } catch (e) {
        message.error(e?.error || e?.message || 'No se pudo reintentar la boleta');
      } finally {
        setEnviando(false);
      }
    };

    return (
      <>
        <Title level={5} style={{ marginTop: 0 }}>Emitir la boleta pendiente</Title>
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Antes de reintentar, comprueba en Bsale que el folio no exista ya."
          description="Un timeout no prueba que Bsale no haya emitido, y una boleta duplicada solo se revierte con nota de crédito." />
        <Button type="primary" loading={enviando} icon={<FileTextOutlined />} onClick={reintentar}>
          Reintentar emisión
        </Button>
      </>
    );
  }

  // ── Cerrar el caso ─────────────────────────────────────────────────────────
  if (salida === 'cerrar') {
    const cerrar = async () => {
      if (caso.bucket === 'boleta' && (!notas || notas.trim().length < 5)) {
        message.warning('Escribe el motivo del cierre'); return;
      }
      setEnviando(true);
      try {
        // Las filas de boleta se cierran por su propia ruta: `resolver` no las acepta.
        if (caso.bucket === 'boleta') {
          await AdminV2.cerrarCasoBoleta(caso.id, { motivo: notas, subtipo: caso.subtipo || subtipoAPI });
        } else {
          await AdminV2.marcarResuelto(subtipoAPI, caso.id, { notas });
        }
        message.success('Caso cerrado');
        onListo();
      } catch (e) {
        message.error(e?.error || e?.message || 'No se pudo cerrar el caso');
      } finally {
        setEnviando(false);
      }
    };

    const sugerido = (detalle?.solucion?.pasos || []).find(p => p.motivoSugerido)?.motivoSugerido;

    return (
      <>
        <Title level={5} style={{ marginTop: 0 }}>Cerrar el caso</Title>
        <Paragraph type="secondary">
          Sale de la bandeja y queda en la auditoría quién lo cerró y por qué. El estado
          de facturación no se altera.
        </Paragraph>
        {sugerido && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Motivo sugerido"
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{sugerido}</Text>
                <Button size="small" onClick={() => setNotas(sugerido)}>Usar este motivo</Button>
              </Space>
            } />
        )}
        <Input.TextArea rows={3} placeholder="Motivo del cierre (queda en la auditoría)"
          value={notas} onChange={e => setNotas(e.target.value)} style={{ marginBottom: 12 }} />
        <Button type="primary" loading={enviando} icon={<CheckCircleOutlined />} onClick={cerrar}>
          Cerrar caso
        </Button>
      </>
    );
  }

  return (
    <>
      <Title level={5} style={{ marginTop: 0 }}>Reembolso · {clp(aDevolver)}</Title>
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Registra por dónde salió la plata. Si además hay que anular una boleta, la nota de
        crédito la emite el cliente desde Bsale: este panel no las emite.
      </Paragraph>

      <Card size="small" title={<Space><CreditCardOutlined />Registrar la devolución</Space>} style={{ marginBottom: 12 }}>
        <Radio.Group value={medio} onChange={e => setMedio(e.target.value)} style={{ marginBottom: 12 }}>
          <Radio.Button value="transbank">Transbank</Radio.Button>
          <Radio.Button value="efectivo">Efectivo en caja</Radio.Button>
          <Radio.Button value="nota_credito">Nota de crédito</Radio.Button>
        </Radio.Group>
        {medio === 'transbank' && detalle?.plata?.auth && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={`Busca la autorización ${detalle.plata.auth}${detalle.plata.tarjeta ? ` · tarjeta •••• ${detalle.plata.tarjeta}` : ''} en el portal de Transbank.`} />
        )}
        <Input.TextArea rows={2} placeholder="Notas (queda en la auditoría)"
          value={notas} onChange={e => setNotas(e.target.value)} style={{ marginBottom: 12 }} />
        <Space>
          <Button loading={enviando} onClick={() => marcar('pendiente')}>Marcar pendiente</Button>
          <Button type="primary" loading={enviando} icon={<CheckCircleOutlined />} onClick={() => marcar('reembolsado')}>
            Marcar reembolsado
          </Button>
        </Space>
      </Card>

      <Card size="small" title={<Space><FileTextOutlined />Boletas del caso</Space>}>
        {docs.length === 0
          ? <Empty description="Este caso no tiene boletas emitidas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="Las notas de crédito las emite solo el cliente desde Bsale. Si hay que anular una boleta, infórmale el folio." />
              <Space direction="vertical">
                {docs.map(d => (
                  <Text key={d.folio}>
                    Folio <Text code copyable>{d.folio}</Text> · {clp(d.monto)} · {d.fecha ? dayjs(d.fecha).format('DD/MM/YYYY') : ''}
                  </Text>
                ))}
              </Space>
            </>
          )}
      </Card>
    </>
  );
}
